import { useMemo } from 'react';
import type { SeriesBarOptions } from 'highcharts';
import { BarChart } from '@forge/charts';
import { useTheme } from '@forge/common';
import type { CategorySummary } from '@/api/dashboard';
import ChartCard from './ChartCard';
import { getStatusColors } from './chartColors';

interface InitiativeProgressProps {
  categories: CategorySummary[];
}

const InitiativeProgress = ({ categories }: InitiativeProgressProps) => {
  const { isDark } = useTheme();

  const series = useMemo<SeriesBarOptions[]>(() => {
    const colors = getStatusColors(isDark);

    return [
      {
        type: 'bar',
        name: 'Done',
        data: categories.map((category) => category.done),
        color: colors.done,
      },
      {
        type: 'bar',
        name: 'In progress',
        data: categories.map((category) => category.inProgress),
        color: colors.in_progress,
      },
      {
        type: 'bar',
        name: 'Todo',
        data: categories.map((category) => category.todo),
        color: colors.todo,
      },
      {
        type: 'bar',
        name: 'Blocked',
        data: categories.map((category) => category.blocked),
        color: colors.blocked,
      },
    ];
  }, [categories, isDark]);

  const chartHeight = Math.max(220, categories.length * 56 + 64);

  return (
    <ChartCard
      title="Initiative progress"
      subtitle="Milestone distribution across strategic platform initiatives"
    >
      <BarChart
        series={series}
        chart={{ height: chartHeight, spacing: [12, 16, 8, 8] }}
        xAxis={{
          categories: categories.map((category) => category.category),
          tickLength: 0,
        }}
        yAxis={{
          title: { text: undefined },
          allowDecimals: false,
          reversedStacks: false,
        }}
        legend={{ align: 'left', verticalAlign: 'top', symbolRadius: 6 }}
        tooltip={{ shared: false }}
        plotOptions={{
          series: { stacking: 'normal' },
          bar: { borderRadius: 3, borderWidth: 0 },
        }}
      />
    </ChartCard>
  );
};

export type { InitiativeProgressProps };
export default InitiativeProgress;
